import { Alert, Button } from "antd"
import dayjs from "dayjs"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { useAppDispatch } from "../../../../hooks/use-app-dispatch.ts"
import { useDateFormat } from "../../../../hooks/use-date-format.ts"
import { DATE_FORMAT } from "../../../../utils/date/date.ts"
import { restoreProject } from "../../../Projects/redux/projects.thunks.ts"
import { fetchProject } from "../../redux/project.thunks.ts"
import { type Project } from "../../types/project.types.ts"

// The detail endpoint sends DRF's plain datetime; a serializer that localises
// it sends the account's own format with a time after it.
const archivedOn = (value: string, userFormat: string | null) =>
  ["YYYY-MM-DD HH:mm:ss", userFormat && `${userFormat} HH:mm`, userFormat]
    .filter((format): format is string => !!format)
    .map((format) => dayjs(value, format, true))
    .find((date) => date.isValid()) ?? null

interface ProjectArchiveBannerProps {
  project: Project
  canRestore: boolean
}

/**
 * Says an archived project is archived and read-only, and offers Restore in
 * place. Without it the page looked like any active project whose buttons had
 * gone missing. Archived leads already open the same way.
 */
export function ProjectArchiveBanner({
  project,
  canRestore
}: ProjectArchiveBannerProps) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [restoring, setRestoring] = useState(false)
  const { userFormat } = useDateFormat()

  if (!project?.is_archived) return null

  const since = project.archived_at
    ? archivedOn(project.archived_at, userFormat)
    : null

  const facts = [
    project.archive_reason_name &&
      t("projectArchive.banner.reason", {
        reason: project.archive_reason_name
      }),
    since &&
      t("projectArchive.banner.since", {
        date: since.format(userFormat || DATE_FORMAT)
      })
  ].filter(Boolean)

  const handleRestore = async () => {
    setRestoring(true)
    try {
      await dispatch(restoreProject(project.id)).unwrap()
      // The edit rights come back from the reloaded record, not from here.
      await dispatch(fetchProject(project.id))
    } catch {
      // The thunk has already shown why the restore was refused.
    } finally {
      setRestoring(false)
    }
  }

  return (
    <Alert
      type={"info"}
      showIcon
      role={"alert"}
      data-testid={"project-archive-banner"}
      message={t("projectArchive.banner.title")}
      description={[...facts, t("projectArchive.banner.readOnly")].join(" · ")}
      action={
        canRestore ? (
          <Button
            size={"small"}
            type={"primary"}
            loading={restoring}
            onClick={handleRestore}
          >
            {t("projectArchive.banner.restore")}
          </Button>
        ) : null
      }
    />
  )
}
